import React from 'react'
import CustomLink from './customLink'

/**
 * Default rich text serializer components for PrismicRichText.
 *
 * Provided globally via PrismicProvider in gatsby-browser.tsx and gatsby-ssr.tsx,
 * so all PrismicRichText instances inherit these serializers automatically.
 * Individual components can still override specific serializers by passing their
 * own `components` prop — local definitions take priority over these defaults.
 */

/** Pixels per indent level applied to first-line text-indent. */
const INDENT_PX = 24

/**
 * Detects leading whitespace in a paragraph's raw text and returns an indent level.
 * Tabs are normalized to 4 spaces; every 2 whitespace characters = 1 indent level.
 * Returns 0 when no leading whitespace is found.
 */
const getIndentLevel = (text: string): number => {
  const match = text.match(/^([\t \u00a0]+)/)
  if (!match) return 0

  const normalized = match[1].replace(/\t/g, '    ')
  return Math.max(1, Math.floor(normalized.length / 2))
}

const richTextComponents = {
  /**
   * Custom paragraph serializer that preserves first-line indentation
   * added by content authors in the Prismic rich text editor.
   * Browsers collapse HTML whitespace by default, so this detects leading
   * spaces/tabs in the raw text and applies CSS text-indent to restore them.
   */
  paragraph: ({ node, children, key }: any) => {
    const indentLevel = getIndentLevel(node.text)
    if (indentLevel > 0) {
      return (
        <p key={key} style={{ textIndent: `${indentLevel * INDENT_PX}px` }}>
          {children}
        </p>
      )
    }
    return <p key={key}>{children}</p>
  },
  /** Routes Prismic hyperlinks through the project's CustomLink resolver. */
  hyperlink: ({ node, children }: any) =>
    CustomLink({ link: node.data, children }),
}

export default richTextComponents
